/**
 * Accessible, dismissible consent dialog, centered as a modal with an overlay.
 * Compact by default, like the store's earlier banner: title, description and
 * two actions, "Show preferences" and "Accept" (accepts all purposes).
 * Showing preferences reveals the categories and services; the first action
 * then saves the toggles ("Essential only" while every optional toggle is off,
 * "Save selection" once one is on) and the main action reads "Accept all".
 * A quiet "Hide preferences" link returns to the compact view. Every entry point
 * (automatic banner, cookie icon, footer control) opens the compact view first.
 * Optional toggles start off. Closing leaves consent unanswered; saves close only after Shopify
 * confirms. A fixed header and footer frame a body that scrolls on its own.
 */
class ConsentDialog extends HTMLElement {
  /** Bind delegated click/change handlers and native-dialog cancellation. */
  constructor() {
    super();
    this.dialog = null;
    this.isPreferencesMode = false;
    this.preferencesVisible = false;
    this.saving = false;
    this.activeButton = null;
    this.translations = {};
    this.handleClick = this.handleClick.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
  }

  /** Render once and attach handlers when Shopify upgrades the custom element. */
  connectedCallback() {
    this.loadTranslations();
    this.render();
    this.dialog = this.querySelector('dialog');
    this.addEventListener('click', this.handleClick);
    this.addEventListener('change', this.handleChange);
    this.dialog.addEventListener('cancel', this.handleCancel);
    document.addEventListener('keydown', this.handleKeydown);
    this.initToggles();
  }

  /** Remove owned listeners if the element is removed by the theme editor. */
  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('change', this.handleChange);
    this.dialog?.removeEventListener('cancel', this.handleCancel);
    document.removeEventListener('keydown', this.handleKeydown);
  }

  /** Reflect actual visitor choices; regional permission does not precheck a toggle. */
  initToggles() {
    const state = window.privacy?.selection() || {};
    for (const type of ['analytics', 'marketing', 'preferences']) {
      const input = this.querySelector('input[data-consent-type="' + type + '"]');
      if (input) input.checked = state[type] === true;
    }
    this.updateSaveLabel();
  }

  /** Name the second action after what it will save. */
  updateSaveLabel() {
    const button = this.querySelector('[data-action="save"]');
    if (!button) return;
    const anyOptional = ['analytics', 'marketing', 'preferences']
      .some(type => this.querySelector('input[data-consent-type="' + type + '"]')?.checked);
    button.textContent = this.t(anyOptional ? 'dialog.save' : 'dialog.reject');
  }

  /** Keep the second action's label in step with the toggles. */
  handleChange(event) {
    if (event.target.matches?.('input[data-consent-type]')) this.updateSaveLabel();
  }

  /** Dispatch explicit actions; dismissal never invokes a consent write. */
  handleClick(event) {
    const button = event.target.closest('[data-action]');
    if (!button || !this.contains(button) || this.saving) return;
    const action = button.dataset.action;
    // Remember which action is saving so it stays highlighted while the others are disabled.
    if (action === 'accept' || action === 'save') this.activeButton = button;
    if (action === 'close') this.hide();
    if (action === 'preferences') this.handleShowPreferences();
    if (action === 'collapse') this.handleHidePreferences();
    if (action === 'accept') this.handleAccept();
    if (action === 'save') this.handleSave();
  }

  /** Reveal the categories; focus moves to the first choice because the clicked button is replaced. */
  handleShowPreferences() {
    this.setPreferencesVisible(true);
    this.querySelector('input[data-consent-type="preferences"]')?.focus({preventScroll: true});
  }

  /** Return to the compact view; unsaved toggles are kept until a save or reload. */
  handleHidePreferences() {
    this.setPreferencesVisible(false);
    this.querySelector('[data-action="preferences"]')?.focus({preventScroll: true});
  }

  /** Swap compact and expanded actions and keep aria-expanded synchronized. */
  setPreferencesVisible(visible) {
    this.preferencesVisible = visible;
    this.querySelector('.consent-categories').hidden = !visible;
    this.querySelector('[data-action="preferences"]').hidden = visible;
    this.querySelector('[data-action="preferences"]').setAttribute('aria-expanded', String(visible));
    this.querySelector('[data-action="save"]').hidden = !visible;
    this.querySelector('[data-action="collapse"]').hidden = !visible;
    // "Accept" in the compact view; "Accept all" next to the toggles, so it cannot read as "accept my selection".
    this.querySelector('[data-action="accept"]').textContent = this.t(visible ? 'dialog.acceptAll' : 'dialog.accept');
    const body = this.querySelector('.consent-body');
    if (body) body.scrollTop = 0;
  }

  /** Escape dismisses without manufacturing a consent decision. */
  handleCancel(event) {
    event.preventDefault();
    if (!this.saving) this.hide();
  }

  /** Escape dismissal while focus is in the dialog, without relying on cancel support. */
  handleKeydown(event) {
    if (event.key === 'Escape' && this.dialog?.open && this.contains(document.activeElement)) {
      this.handleCancel(event);
    }
  }

  /** Grant all cookie purposes; data-sale opt-out remains independent in Shopify. */
  handleAccept() {
    return this.save({analytics: true, marketing: true, preferences: true});
  }

  /** Save the toggles as shown; with all optional toggles off this is essential only. */
  handleSave() {
    const choice = {};
    for (const type of ['analytics', 'marketing', 'preferences']) {
      choice[type] = this.querySelector('input[data-consent-type="' + type + '"]')?.checked === true;
    }
    return this.save(choice);
  }

  /**
   * Prevent duplicate submits and expose pending/error states. A failed save
   * keeps the dialog open; retry is a fresh visitor interaction with these actions.
   */
  async save(choice) {
    if (this.saving) return;
    this.setBusy(true);
    // Announced to screen readers but not drawn, so the dialog keeps its size mid-save.
    this.setMessage(this.t('dialog.saving'), false, true);
    try {
      if (!window.privacy) throw new Error('Consent manager unavailable');
      await window.privacy.updateConsent(choice);
      this.hide();
    } catch (error) {
      this.setMessage(this.t('dialog.saveError'), true);
      console.warn('[ConsentDialog] Shopify consent save failed:', error.message);
    } finally {
      this.setBusy(false);
    }
  }

  /** Disable competing choices while a save is pending; the clicked action stays highlighted. */
  setBusy(busy) {
    this.saving = busy;
    this.dialog.setAttribute('aria-busy', String(busy));
    for (const button of this.querySelectorAll('button')) button.disabled = busy;
    for (const input of this.querySelectorAll('input[data-consent-type]')) {
      input.disabled = busy || input.dataset.consentType === 'essential';
    }
    this.activeButton?.classList.toggle('consent-btn--busy', busy);
    if (!busy) this.activeButton = null;
  }

  /**
   * Render a localized status using textContent in a live region. Quiet messages
   * are announced to screen readers without taking space in the dialog.
   */
  setMessage(message, error = false, quiet = false) {
    const status = this.querySelector('[data-consent-status]');
    status.textContent = message;
    status.hidden = !message;
    status.classList.toggle('consent-status--error', error);
    status.classList.toggle('consent-status--quiet', quiet);
  }

  /**
   * Show a centered modal dialog: the page behind is inert, dimmed by the
   * overlay and scroll-locked (CSS). Focus moves into the dialog rather than
   * onto an action, so Enter cannot accept by accident; preferences opened by
   * the visitor focus the first optional choice.
   */
  show(preferencesMode = false, returnFocus = document.activeElement) {
    this.isPreferencesMode = preferencesMode;
    this._returnFocus = returnFocus;
    this.initToggles();
    // Always open compact, like the store's earlier banner, whichever control opened it.
    this.setPreferencesVisible(false);
    this.setMessage(window.privacy?.status().phase === 'error' ? this.t('dialog.unavailable') : '');
    if (!this.dialog.open) this.dialog.showModal();
    if (preferencesMode) {
      // Opened from the cookie icon or footer: the next step is Show preferences.
      this.querySelector('[data-action="preferences"]')?.focus({preventScroll: true});
    } else {
      this.querySelector('.consent-dialog-inner')?.focus({preventScroll: true});
    }
  }

  /** Reopen from the settings controls (compact view, focus on Show preferences). */
  showPreferences(returnFocus) { this.show(true, returnFocus); }

  /** Dismiss without writing; restore the floating settings control and prior focus. */
  hide() {
    this.dialog?.close();
    // Reveal the settings control before restoring focus to it.
    this.dispatchEvent(new CustomEvent('consent-dialog-closed', {bubbles: true}));
    if (this.isPreferencesMode) this._returnFocus?.focus({preventScroll: true});
  }

  /** Read Liquid's localized data, with complete English fallbacks on parse failure. */
  loadTranslations() {
    try {
      this.translations = JSON.parse(document.getElementById('consent-translations')?.textContent || '{}');
    } catch (_) {
      this.translations = {};
    }
  }

  /** Return a localized string, falling back to readable text rather than a key. */
  t(key) {
    const fallbacks = {
      'dialog.title': 'We value your privacy',
      'dialog.description': 'We use cookies to enhance your browsing experience, show you personalized content and offers, and analyze our traffic. Accepting helps us provide a better, more tailored experience for you.',
      'dialog.accept': 'Accept',
      'dialog.acceptAll': 'Accept all',
      'dialog.showPreferences': 'Show preferences',
      'dialog.hidePreferences': 'Hide preferences',
      'dialog.reject': 'Essential only',
      'dialog.save': 'Save selection',
      'dialog.close': 'Close cookie banner',
      'dialog.saving': 'Saving your choices…',
      'dialog.saveError': 'We could not confirm your choices. Please try again. You can continue browsing.',
      'dialog.unavailable': 'Cookie settings are temporarily unavailable. You can continue browsing or try saving your choices again.',
      'categories.essential.title': 'Essential',
      'categories.essential.description': 'Required for the store to function.',
      'categories.preferences.title': 'Preferences',
      'categories.preferences.description': 'Remember your settings and preferences.',
      'categories.analytics.title': 'Analytics',
      'categories.analytics.description': 'Help us understand how visitors use our store.',
      'categories.marketing.title': 'Marketing',
      'categories.marketing.description': 'Allow relevant ads and offers.',
      'links.privacyPolicy': 'Privacy policy',
      'links.cookiePolicy': 'Cookie policy',
      'links.shopifyPrivacyPolicy': 'Shopify privacy policy',
      'service': 'service',
      'services': 'services'
    };
    const value = key.split('.').reduce((value, part) => value?.[part], this.translations);
    return typeof value === 'string' && value && !/^translation missing:/i.test(value.trim())
      ? value : fallbacks[key] || '';
  }

  /** Escape translated text/attributes before inserting the dialog's static markup. */
  escape(value) {
    return String(value).replace(/[&<>"']/g, character => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    })[character]);
  }

  /** List the services a category covers in a collapsed disclosure. */
  renderServices(type) {
    const services = (this.translations.trackers?.[type] || [])
      .map(service => typeof service === 'string' ? service : service?.name)
      .filter(Boolean);
    if (!services.length) return '';
    const label = services.length + ' ' + this.t(services.length === 1 ? 'service' : 'services');
    return '<details class="consent-services"><summary class="consent-services-summary">' +
      '<svg class="consent-services-caret" viewBox="0 0 10 6" aria-hidden="true" focusable="false"><path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
      '<span>' + this.escape(label) + '</span></summary>' +
      '<ul class="consent-services-list">' + services.map(name => '<li>' + this.escape(name) + '</li>').join('') + '</ul></details>';
  }

  /** Render an accessible category; only essential cookies are locked on. */
  renderCategory(type, essential = false) {
    const title = this.escape(this.t('categories.' + type + '.title'));
    return '<div class="consent-category"><div class="consent-category-header">' +
      '<div class="consent-category-info"><span class="consent-category-title" id="consent-label-' + type + '">' + title + '</span>' +
      '<span class="consent-category-desc" id="consent-desc-' + type + '">' + this.escape(this.t('categories.' + type + '.description')) + '</span></div>' +
      '<label class="consent-toggle"><input type="checkbox" class="consent-toggle-input" data-consent-type="' + type + '" ' +
      'aria-labelledby="consent-label-' + type + '" aria-describedby="consent-desc-' + type + '"' +
      (essential ? ' checked disabled' : '') + '><span class="consent-toggle-slider" aria-hidden="true"></span></label></div>' +
      this.renderServices(type) + '</div>';
  }

  /** Fixed header and footer around a scrolling body of description and categories. */
  render() {
    const text = key => this.escape(this.t(key));
    const policyUrl = this.escape(this.translations.privacyPolicyUrl || '/policies/privacy-policy');
    const cookieUrl = this.translations.cookiePolicyUrl ? this.escape(this.translations.cookiePolicyUrl) : '';
    this.innerHTML = '<dialog class="consent-dialog" aria-labelledby="consent-title" aria-describedby="consent-description">' +
      '<div class="consent-dialog-inner" tabindex="-1">' +
      '<div class="consent-header"><button type="button" data-action="close" class="consent-close-btn" aria-label="' + text('dialog.close') + '">' +
      '<svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 18L18 6M6 6l12 12"/></svg></button>' +
      '<h2 class="consent-title" id="consent-title">' + text('dialog.title') + '</h2></div>' +
      '<div class="consent-body"><p class="consent-description" id="consent-description">' + text('dialog.description') + '</p>' +
      '<div class="consent-categories" id="consent-categories" hidden>' +
      ['essential', 'preferences', 'analytics', 'marketing'].map(type => this.renderCategory(type, type === 'essential')).join('') + '</div></div>' +
      '<div class="consent-footer">' +
      '<p class="consent-status" data-consent-status role="status" aria-live="polite" aria-atomic="true" hidden></p>' +
      '<div class="consent-buttons"><button type="button" data-action="preferences" class="button button--secondary consent-btn" aria-expanded="false" aria-controls="consent-categories">' + text('dialog.showPreferences') + '</button>' +
      '<button type="button" data-action="save" class="button button--secondary consent-btn" hidden>' + text('dialog.reject') + '</button>' +
      '<button type="button" data-action="accept" class="button consent-btn consent-btn--primary">' + text('dialog.accept') + '</button></div>' +
      '<div class="consent-links"><button type="button" data-action="collapse" class="consent-link-btn" aria-controls="consent-categories" hidden>' + text('dialog.hidePreferences') + '</button>' +
      '<a href="' + policyUrl + '">' + text('links.privacyPolicy') + '</a>' +
      (cookieUrl ? '<a href="' + cookieUrl + '">' + text('links.cookiePolicy') + '</a>' : '') +
      '<a href="https://www.shopify.com/legal/privacy/consumers">' + text('links.shopifyPrivacyPolicy') + '</a>' +
      '</div></div></div></dialog>';
  }
}

if (!customElements.get('consent-dialog')) customElements.define('consent-dialog', ConsentDialog);

/** Initialize after deferred scripts/DOM parsing; Shopify readiness controls visibility. */
async function initializeConsentDialog() {
  const dialog = document.getElementById('consent-dialog');
  if (!dialog || dialog.dataset.initialized === 'true') return;
  dialog.dataset.initialized = 'true';
  const aside = document.getElementById('consent-cookie-aside');
  const showAside = () => aside?.classList.add('consent-cookie-aside--visible');
  let preferencesRequested = false;
  let startupPending = true;
  const openPreferences = () => {
    preferencesRequested = true;
    const returnFocus = document.activeElement;
    aside?.classList.remove('consent-cookie-aside--visible');
    dialog.showPreferences(returnFocus);
  };
  aside?.querySelector('button')?.addEventListener('click', openPreferences);
  document.addEventListener('click', event => {
    if (event.target.closest('[data-consent-open]')) openPreferences();
  });
  document.addEventListener('gct:open-consent-preferences', openPreferences);
  dialog.addEventListener('consent-dialog-closed', showAside);
  document.addEventListener('gct:consent-changed', event => {
    // Late conflicting Shopify writes need a fresh visitor action, never replay.
    if (event.detail.status.phase === 'error') {
      // Initial API failure must not reopen preferences dismissed while loading.
      // Later save/conflict errors still use the recovery UI below.
      if (startupPending && preferencesRequested && !dialog.dialog.open) return;
      if (!dialog.dialog.open) {
        aside?.classList.remove('consent-cookie-aside--visible');
        dialog.showPreferences();
      }
      dialog.setMessage(dialog.t('dialog.saveError'), true);
    } else if (event.detail.status.phase === 'ready' && !dialog.saving) {
      // Read-only recovery clears stale errors without reopening a dismissed
      // banner, changing unsaved toggles or interrupting a visitor's save.
      dialog.setMessage('');
    }
  });
  try {
    await window.privacy?.ready;
    if (preferencesRequested) return;
    const showBanner = !window.privacy || await window.privacy.shouldShowBanner();
    // A footer action can happen while Shopify is still loading. Preserve the
    // requested view, unsaved toggles and dismissal when startup catches up.
    if (preferencesRequested) return;
    if (showBanner) dialog.show();
    else showAside();
  } catch (_) {
    if (!preferencesRequested) dialog.show();
  } finally {
    startupPending = false;
  }
}
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeConsentDialog, {once: true});
} else {
  initializeConsentDialog();
}
